import React, { useEffect, useMemo, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import Tabs from '@cloudscape-design/components/tabs';
import Badge from '@cloudscape-design/components/badge';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import Alert from '@cloudscape-design/components/alert';
import Avatar from '../components/Avatar';
import { getSupplierControlService } from '../services';
import type {
    ControlSite,
    ControlRequest,
    ControlUser,
    OnboardedSupplier,
    UserType,
} from '../services/types';

// Supplier Control — internal access management. Ported from the dscs
// prototypes (Suppliers directory + Approval Queue + Supplier Review), wired to
// the SupplierControlService. Data is empty until the backend is built.

type ControlView = 'suppliers' | 'queue' | 'review' | 'users';

function fmtDate(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/** A name + email cell with an initials avatar, shared by the tables. */
function PartyCell({ name, email, seed }: { name: string; email: string; seed: string }) {
    return (
        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Avatar name={name} seed={seed} />
            <SpaceBetween size="xxxs">
                <Box fontWeight="bold">{name}</Box>
                <Box color="text-body-secondary" fontSize="body-s">
                    {email}
                </Box>
            </SpaceBetween>
        </SpaceBetween>
    );
}

// ── Onboarded suppliers directory ─────────────────────────────────────────────
function SuppliersView({ suppliers }: { suppliers: OnboardedSupplier[] }) {
    const [q, setQ] = useState('');
    const query = q.trim().toLowerCase();
    const shown = suppliers.filter(
        (s) =>
            query === '' ||
            s.company.toLowerCase().includes(query) ||
            s.email.toLowerCase().includes(query),
    );

    return (
        <Table
            variant="container"
            items={shown}
            filter={
                <TextFilter
                    filteringText={q}
                    filteringPlaceholder="Search by supplier or email…"
                    onChange={({ detail }) => setQ(detail.filteringText)}
                />
            }
            header={
                <Header counter={`(${suppliers.length})`}>
                    Onboarded suppliers
                </Header>
            }
            empty={
                <Box textAlign="center" color="text-body-secondary" padding="l">
                    {suppliers.length === 0
                        ? 'No onboarded suppliers yet. Approve sign-ups from the Approval Queue.'
                        : 'No matching suppliers.'}
                </Box>
            }
            columnDefinitions={[
                {
                    id: 'supplier',
                    header: 'Supplier',
                    cell: (s) => <PartyCell name={s.company} email={s.email} seed={s.id} />,
                },
                {
                    id: 'sites',
                    header: 'Sites',
                    cell: (s) => <Badge color="blue">{`${s.sites} site${s.sites === 1 ? '' : 's'}`}</Badge>,
                },
                {
                    id: 'status',
                    header: 'Status',
                    cell: () => <StatusIndicator type="success">Active</StatusIndicator>,
                },
                {
                    id: 'onboarded',
                    header: 'Onboarded',
                    cell: (s) => fmtDate(s.onboarded),
                },
            ]}
        />
    );
}

// ── Approval queue ────────────────────────────────────────────────────────────
function QueueView({
    requests,
    approvingId,
    onApprove,
}: {
    requests: ControlRequest[];
    approvingId: string | null;
    onApprove: (id: string) => void;
}) {
    const [q, setQ] = useState('');
    const query = q.trim().toLowerCase();
    const shown = requests.filter(
        (r) =>
            query === '' ||
            r.email.toLowerCase().includes(query) ||
            r.supplier.toLowerCase().includes(query),
    );

    return (
        <Table
            variant="container"
            items={shown}
            filter={
                <TextFilter
                    filteringText={q}
                    filteringPlaceholder="Search by email or supplier…"
                    onChange={({ detail }) => setQ(detail.filteringText)}
                />
            }
            header={<Header>Approval queue</Header>}
            empty={
                <Box textAlign="center" color="text-body-secondary" padding="l">
                    No pending requests to show yet.
                </Box>
            }
            columnDefinitions={[
                {
                    id: 'supplier',
                    header: 'Supplier',
                    cell: (r) => <PartyCell name={r.supplier} email={r.email} seed={r.id} />,
                },
                {
                    id: 'sites',
                    header: 'Sites',
                    cell: (r) => <Badge color="blue">{`${r.sites} site${r.sites > 1 ? 's' : ''}`}</Badge>,
                },
                {
                    id: 'status',
                    header: 'Status',
                    cell: (r) =>
                        r.verified ? (
                            <StatusIndicator type="pending">
                                Email verified · Awaiting approval
                            </StatusIndicator>
                        ) : (
                            <StatusIndicator type="in-progress">
                                Awaiting email verification
                            </StatusIndicator>
                        ),
                },
                { id: 'submitted', header: 'Submitted', cell: (r) => r.submitted },
                {
                    id: 'actions',
                    header: '',
                    cell: (r) =>
                        r.verified ? (
                            <Button
                                variant="primary"
                                disabled={approvingId !== null}
                                loading={approvingId === r.id}
                                onClick={() => onApprove(r.id)}
                            >
                                Approve
                            </Button>
                        ) : (
                            <Box color="text-body-secondary">Not ready</Box>
                        ),
                },
            ]}
        />
    );
}

// ── Supplier review (per-site grant decisions for one pending login) ──────────
type Decision = 'pending' | 'approved' | 'rejected';

function ReviewView({
    request,
    sites,
    onBack,
}: {
    request: ControlRequest | null;
    sites: ControlSite[];
    onBack: () => void;
}) {
    const allTo = (d: Decision): Record<string, Decision> =>
        Object.fromEntries(sites.map((s) => [s.id, d]));
    const [decisions, setDecisions] = useState<Record<string, Decision>>(allTo('pending'));
    const [done, setDone] = useState(false);
    useEffect(() => setDecisions(Object.fromEntries(sites.map((s) => [s.id, 'pending']))), [sites]);

    const set = (id: string, d: Decision) => setDecisions((prev) => ({ ...prev, [id]: d }));
    const allDecided = sites.length > 0 && sites.every((s) => decisions[s.id] !== 'pending');
    const approvedCount = sites.filter((s) => decisions[s.id] === 'approved').length;

    if (!request) {
        return (
            <Container>
                <Box textAlign="center" padding="l">
                    <SpaceBetween size="s">
                        <Box color="text-body-secondary">No request selected.</Box>
                        <Button variant="link" onClick={onBack}>
                            Back to queue
                        </Button>
                    </SpaceBetween>
                </Box>
            </Container>
        );
    }

    if (done) {
        return (
            <Container>
                <Box textAlign="center" padding="xl">
                    <SpaceBetween size="m">
                        <StatusIndicator type="success">Decision recorded</StatusIndicator>
                        <Box color="text-body-secondary">
                            {request.email} was granted access to {approvedCount} of {sites.length}{' '}
                            site(s).
                        </Box>
                        <Button onClick={onBack}>Back to queue</Button>
                    </SpaceBetween>
                </Box>
            </Container>
        );
    }

    return (
        <SpaceBetween size="l">
            <Button variant="link" iconName="angle-left" onClick={onBack}>
                Back to queue
            </Button>

            <Container
                header={
                    <Header variant="h3" description={request.email}>
                        {request.supplier} <StatusIndicator type="pending">Pending login</StatusIndicator>
                    </Header>
                }
            >
                <SpaceBetween size="m">
                    <Box variant="awsui-key-label">
                        Grant site access · {approvedCount} of {sites.length} granted
                    </Box>
                    {sites.length === 0 ? (
                        <Box textAlign="center" color="text-body-secondary" padding="m">
                            No sites to show yet.
                        </Box>
                    ) : (
                        sites.map((s) => {
                            const d = decisions[s.id];
                            return (
                                <Box key={s.id} padding={{ vertical: 'xs' }}>
                                    <SpaceBetween
                                        size="m"
                                        direction="horizontal"
                                        alignItems="center"
                                    >
                                        <SpaceBetween size="xxxs">
                                            <SpaceBetween
                                                size="xs"
                                                direction="horizontal"
                                                alignItems="center"
                                            >
                                                <Box fontWeight="bold">{s.name}</Box>
                                                <Badge>{s.code}</Badge>
                                            </SpaceBetween>
                                            <Box color="text-body-secondary" fontSize="body-s">
                                                {s.location}
                                            </Box>
                                        </SpaceBetween>
                                        <SpaceBetween
                                            size="xs"
                                            direction="horizontal"
                                            alignItems="center"
                                        >
                                            {d !== 'pending' ? (
                                                <StatusIndicator
                                                    type={d === 'approved' ? 'success' : 'error'}
                                                >
                                                    {d === 'approved' ? 'Access: Yes' : 'Access: No'}
                                                </StatusIndicator>
                                            ) : null}
                                            <Button
                                                variant={d === 'approved' ? 'primary' : 'normal'}
                                                onClick={() => set(s.id, 'approved')}
                                            >
                                                Yes
                                            </Button>
                                            <Button
                                                variant={d === 'rejected' ? 'primary' : 'normal'}
                                                onClick={() => set(s.id, 'rejected')}
                                            >
                                                No
                                            </Button>
                                        </SpaceBetween>
                                    </SpaceBetween>
                                </Box>
                            );
                        })
                    )}
                    {sites.length > 0 ? (
                        <SpaceBetween size="xs" direction="horizontal">
                            <Button onClick={() => setDecisions(allTo('approved'))}>
                                Approve all
                            </Button>
                            <Button onClick={() => setDecisions(allTo('rejected'))}>
                                Reject all
                            </Button>
                        </SpaceBetween>
                    ) : null}
                </SpaceBetween>
            </Container>

            <Box textAlign="right">
                <Button
                    variant="primary"
                    disabled={!allDecided}
                    onClick={() => allDecided && setDone(true)}
                >
                    {allDecided ? 'Confirm decisions' : 'Decide all sites first'}
                </Button>
            </Box>
        </SpaceBetween>
    );
}

// ── Users (ops view: supplier vs employee) ───────────────────────────────────
const USER_TYPE_FILTERS: { id: UserType | 'all'; text: string }[] = [
    { id: 'all', text: 'All' },
    { id: 'supplier', text: 'Suppliers' },
    { id: 'employee', text: 'Employees' },
];

function UsersView({ users }: { users: ControlUser[] }) {
    const [q, setQ] = useState('');
    const [typeFilter, setTypeFilter] = useState<UserType | 'all'>('all');

    const query = q.trim().toLowerCase();
    const shown = users.filter(
        (u) =>
            (typeFilter === 'all' || u.type === typeFilter) &&
            (query === '' ||
                u.email.toLowerCase().includes(query) ||
                u.username.toLowerCase().includes(query)),
    );

    const supplierCount = users.filter((u) => u.type === 'supplier').length;
    const employeeCount = users.filter((u) => u.type === 'employee').length;

    return (
        <Table
            variant="container"
            items={shown}
            filter={
                <SpaceBetween size="s">
                    <SpaceBetween size="xs" direction="horizontal">
                        <Badge color="blue">{`${supplierCount} suppliers`}</Badge>
                        <Badge color="grey">{`${employeeCount} employees`}</Badge>
                    </SpaceBetween>
                    <SpaceBetween size="s" direction="horizontal" alignItems="center">
                        <SegmentedControl
                            selectedId={typeFilter}
                            onChange={({ detail }) =>
                                setTypeFilter(detail.selectedId as UserType | 'all')
                            }
                            options={USER_TYPE_FILTERS}
                        />
                        <TextFilter
                            filteringText={q}
                            filteringPlaceholder="Search by email or username…"
                            onChange={({ detail }) => setQ(detail.filteringText)}
                        />
                    </SpaceBetween>
                </SpaceBetween>
            }
            header={<Header>Users</Header>}
            empty={
                <Box textAlign="center" color="text-body-secondary" padding="l">
                    No users to show yet.
                </Box>
            }
            columnDefinitions={[
                {
                    id: 'user',
                    header: 'User',
                    cell: (u) => (
                        <SpaceBetween size="xxxs">
                            <Box fontWeight="bold">{u.email}</Box>
                            <Box color="text-body-secondary" fontSize="body-s">
                                {u.username}
                            </Box>
                        </SpaceBetween>
                    ),
                },
                {
                    id: 'type',
                    header: 'Type',
                    cell: (u) => (
                        <Badge color={u.type === 'employee' ? 'grey' : 'blue'}>
                            {u.type === 'employee' ? 'Employee' : 'Supplier'}
                        </Badge>
                    ),
                },
                {
                    id: 'roles',
                    header: 'Roles',
                    cell: (u) => (u.roles.length ? u.roles.join(', ') : '—'),
                },
                {
                    id: 'status',
                    header: 'Status',
                    cell: (u) => (
                        <StatusIndicator type={u.enabled ? 'success' : 'stopped'}>
                            {u.enabled ? u.status : 'Disabled'}
                        </StatusIndicator>
                    ),
                },
                { id: 'created', header: 'Created', cell: (u) => u.created },
            ]}
        />
    );
}

// ── Main Control area ─────────────────────────────────────────────────────────
export default function SupplierControlScreen() {
    const [view, setView] = useState<ControlView>('suppliers');
    const [suppliers, setSuppliers] = useState<OnboardedSupplier[]>([]);
    const [requests, setRequests] = useState<ControlRequest[]>([]);
    const [users, setUsers] = useState<ControlUser[]>([]);
    const [reviewId, setReviewId] = useState<string | null>(null);
    const [approvingId, setApprovingId] = useState<string | null>(null);
    const [approveError, setApproveError] = useState<string | null>(null);

    const approve = (id: string) => {
        setApprovingId(id);
        setApproveError(null);
        getSupplierControlService()
            .approveRequest(id)
            .then((refreshed) => setRequests(refreshed))
            .catch((e) =>
                setApproveError(e instanceof Error ? e.message : 'Failed to approve request.'),
            )
            .finally(() => setApprovingId(null));
    };

    useEffect(() => {
        let live = true;
        const svc = getSupplierControlService();
        Promise.all([svc.listOnboardedSuppliers(), svc.listRequests(), svc.listUsers()])
            .then(([s, r, u]) => {
                if (live) {
                    setSuppliers(s);
                    setRequests(r);
                    setUsers(u);
                }
            })
            .catch(() => {
                if (live) {
                    setSuppliers([]);
                    setRequests([]);
                    setUsers([]);
                }
            });
        return () => {
            live = false;
        };
    }, []);

    const reviewRequest = useMemo(
        () => requests.find((r) => r.id === reviewId) ?? null,
        [requests, reviewId],
    );
    // Per-site review needs the sites model, which is a separate follow-up; no
    // sites are available yet, so the review screen shows its empty state.
    const reviewSites: ControlSite[] = useMemo(() => [], []);

    const pendingCount = requests.filter((r) => r.verified).length;

    // The review sub-view is reached from the queue; it isn't its own tab.
    if (view === 'review') {
        return (
            <ReviewView
                request={reviewRequest}
                sites={reviewSites}
                onBack={() => {
                    setView('queue');
                    setReviewId(null);
                }}
            />
        );
    }

    return (
        <Tabs
            activeTabId={view}
            onChange={({ detail }) => {
                setView(detail.activeTabId as ControlView);
                setReviewId(null);
            }}
            tabs={[
                {
                    id: 'suppliers',
                    label: 'Onboarded Suppliers',
                    content: <SuppliersView suppliers={suppliers} />,
                },
                {
                    id: 'queue',
                    label: pendingCount > 0 ? `Approval Queue (${pendingCount})` : 'Approval Queue',
                    content: (
                        <SpaceBetween size="s">
                            {approveError ? <Alert type="error">{approveError}</Alert> : null}
                            <QueueView
                                requests={requests}
                                approvingId={approvingId}
                                onApprove={approve}
                            />
                        </SpaceBetween>
                    ),
                },
                {
                    id: 'users',
                    label: 'Users',
                    content: <UsersView users={users} />,
                },
            ]}
        />
    );
}
